import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../src/app/store';
import { Constants } from '../../constants/create';
import { axiosApiCall } from '../../services/apiService';
import { getDefaultedFeaturesAndServices } from '../../services/getDataFromDefaulted';
import {
  ApiResponse,
  BeneficialOwnerFields,
  DocumentManagementFile,
  Parties,
  PrefillData,
  OfficerFields,
  ReferenceData,
  IFeaturesAndServices,
  ICashSweep,
  IReportingParameters,
  IFundingDetails,
  ITaxParameter,
  RetrieveResultSet,
  AuthorizedAgentsFormFields,
  StepConfig,
  RequestBody,
  IReportingAnalytics,
  IDataFeed,
  RetrieveAccountResultSet,
  OfficerRole,
  ClubbedEntityDetails,
  CoOwnerFields,
  CreateDraftDetails,
  AccountDetails,
  AccountAccessData,
  DraftDetails,
  NumberType,
  DataInformation,
  DraftResponse,
  ISelectedParty,
  DraftAccountDetails,
  FeaturesAndServices,
  FiduciaryFields,
  DocumentManagementFolder,
  ClientUploadStorePayload,
  UploadDocument,
  SignerPartyDetails,
  IPowerHoldingTable,
  Address,
} from '../../utils/types';

export interface CreateState {
  draftDetails: DraftDetails;
  entityRetrieveDetails: RetrieveResultSet;
  accountRetrieveDetails: RetrieveAccountResultSet;
  defaultEntityDetails: ClubbedEntityDetails;
  entityDetails: ClubbedEntityDetails;
  parties: Parties;
  clientUploadedDocuments: DocumentManagementFile;
  accountDetails: AccountDetails;
  featuresAndServices: IFeaturesAndServices;
  prefillData: PrefillData;
  signerPartyDetails: SignerPartyDetails;
  stepConfigData: StepConfig[];
  encryptionKey: string;
  isEditMode: boolean;
  isMaintenanceRequired: boolean;
}

const initialState: CreateState = {
  draftDetails: {
    draftId: '',
    draftStage: '',
    createdDate: '',
    lastModifiedByDate: '',
    draftStatus: '',
    leDraftUUID: '',
    accountDraftUUID: '',
    lgDraftUUID: '',
    ultimateParentUlek: '',
    isDraftSaving: false
  },
  entityRetrieveDetails: {
    partyType: '',
    basicAttributeRules: [],
    addressRules: [],
    orgAttributeRules: [],
    corporateAttributeRules: [],
    partyRules: [],
    documentRules: [],
    basicDetailsRules: [],
    contactDetailsRules: [],
  },
  accountRetrieveDetails: {
    accountType: '',
    accountAccessRule: {},
    accountBasicAttributeRules: [],
    accountHoldingRule: {},
    assetManagerRule: {},
    basicContactProfileRule: {},
    proxyRecipientRule: {},
    fundingDetailsRule: {},
    addOnServicesRules: {},
  },
  entityDetails: {},
  parties: {
    officers: [],
    fiduciary: [],
    powerHoldingRole: [],
    beneficialOwners: [],
    authorizedAgents: [],
    selectedParty: {
      openModal: false,
    },
    selectedControlPerson: {
      id: '',
      officerId: '',
      ssn: '',
      partyType: '',
    },
    selectedCorporateSecretary: {
      id: '',
      officerId: '',
      ssn: '',
      partyType: '',
    },
    beneficialOwnerCheck: false,
    coOwners: [],
    documentFolderDetails: [],
  },
  clientUploadedDocuments: {},
  accountDetails: {},
  featuresAndServices: {
    featuresSetDefault: false,
    servicesSetDefault: false,
    cashSweepOptions: [],
    reportingParameters: [],
    fundingData: [],
    taxParameters: [],
    reportingAnalytics: [],
    dataFeed: [],
  },
  prefillData: {
    countryList: [],
    statesList: [],
    subLegalEntityList: [],
    requiredDocumentsList: [],
    citizenshipList: [],
    qualificationCode: [],
    referenceData: {},
    subLegalEntityListCorp: [],
    subLegalEntityListTrust: [],
    mailingAddressesList: [],
  },
  defaultEntityDetails: {},
  signerPartyDetails: {},
  stepConfigData: Constants.createSteps,
  encryptionKey: '',
  isEditMode: false,
  isMaintenanceRequired: false,
};

export const saveDraftData = createAsyncThunk('asyncSaveDraft', async (body: RequestBody<any>) => {
  const apiUrl: string = process.env.UXP_DRAFT_URL as string;
  const responseData: ApiResponse = await axiosApiCall('POST', `${apiUrl}/draft`, body);
  return responseData.data;
});

export const createDocumentManagementFolder = createAsyncThunk('asyncCreateDcoFolders', async (body: RequestBody<any>) => {
  const apiUrl: string = process.env.UXP_DOCUMENT_MANAGEMENT as string;
  const responseData: ApiResponse = await axiosApiCall('POST', `${apiUrl}/document-folders/parties`, body);
  if (responseData?.meta?.records !== 0) {
    return responseData.data.resultSet;
  }
});

export const fetchReferenceCountries = createAsyncThunk('asyncFetchCountries', async () => {
  const apiUrl: string = process.env.UXP_RULES_URL as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/reference-information?type=country`);
  return responseData.data;
});
export const fetchReferenceDataInformation = createAsyncThunk('asyncFetchReferenceData', async () => {
  const apiUrl: string = process.env.UXP_RULES_URL as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/reference-data-information`);
  return responseData.data;
});

export const fetchReferenceStates = createAsyncThunk('asyncFetchStates', async (params: string) => {
  const apiUrl: string = process.env.UXP_RULES_URL as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/reference-information?type=states&${params}`);
  return responseData.data;
});

export const fetchReferenceSubLegalEntity = createAsyncThunk('asyncFetchSubLegalEntity', async (type: string) => {
  const apiUrl: string = process.env.UXP_RULES_URL as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/reference-information?type=${type}`);
  return responseData.data;
});

export const fetchMailingAddresses = createAsyncThunk('asyncFetchMailingAddresses', async (partyId: string) => {
  const apiUrl: string = process.env.UXP_SEARCH_AND_PREFILL as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/mailing-addresses?partyId=${partyId}`);
  return responseData.data;
});

export const fetchAllEntitySubTypes = createAsyncThunk('asyncFetchAllEntitySubTypes', async () => {
  const apiUrl: string = process.env.UXP_RULES_URL as string;
  const corpResponseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/reference-information?type=corpSubLegalEntity`);
  const trustResponseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/reference-information?type=trustSubLegalEntity`);
  return { corpEntityResultSet: corpResponseData.data.resultSet, trustEntityResultSet: trustResponseData.data.resultSet };
});


export const fetchReferenceQualificationCode = createAsyncThunk('asyncFetchQualificationCode', async () => {
  const apiUrl: string = process.env.UXP_RULES_URL as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/reference-information?type=QualificationCode`);
  return responseData.data;
});

export const fetchRequiredDocuments = createAsyncThunk(
  'asyncFetchRequiredDocuments',
  async (body: RequestBody<object>) => {
    const apiUrl: string = process.env.UXP_RULES_URL as string;
    const responseData: ApiResponse = await axiosApiCall('POST', `${apiUrl}/rule/party/docs`, body);
    if (responseData?.data?.resultSet) {
      return responseData.data.resultSet;
    }
  }
);

export const fetchCoOwnershipList = createAsyncThunk('asyncFetchCoOwnershipList', async (body: RequestBody<object>) => {
  const apiUrl: string = process.env.UXP_RULES_URL as string;
  const responseData: ApiResponse = await axiosApiCall('POST', `${apiUrl}/rule/party/party-compliance-rules`, body);
  if (responseData?.data?.resultSet) {
    return responseData.data.resultSet;
  }
});

export const retrieveLegalEntity = createAsyncThunk('asyncRetrieveLegalEntity', async (body: any) => {
  const apiUrl: string = process.env.UXP_RULES_URL as string;
  const responseData: ApiResponse = await axiosApiCall(
    'POST',
    `${apiUrl}/rule/party/user-input-compliance-rules`,
    body
  );
  if (responseData?.data?.resultSet) {
    return responseData.data.resultSet;
  }
});

export const retrieveAccount = createAsyncThunk('asyncRetrieveAccount', async (body: any) => {
  const apiUrl: string = process.env.UXP_RULES_URL as string;
  const responseData: ApiResponse = await axiosApiCall(
    'POST',
    `${apiUrl}/rule/account/user-input-compliance-rules`,
    body
  );
  if (responseData?.data?.resultSet) {
    return responseData.data.resultSet;
  }
});

export const fetchEncryptionKey = createAsyncThunk('asyncRetrieveEncryptionKey', async () => {
  const apiUrl: string = process.env.UXP_ACCESS_MANAGEMENT as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/encryption-key`);
  if (responseData) {
    return responseData;
  }
});

export const fetchDefaultedFeaturesAndServices = createAsyncThunk(
  'asyncRetrieveDefaultedFeaturesAndServices',
  async (ulek: string) => {
    const apiUrl: string = process.env.UXP_CORE_DOMAIN_URL as string;
    const responseData: ApiResponse = await axiosApiCall(
      'GET',
      `${apiUrl}/legal-entity-features-services?ulek=${ulek}`
    );
    if (responseData?.meta?.records !== 0) {
      return responseData.data.resultSet;
    }
  }
);

const setDraftOfficers = (state: CreateState, payload: OfficerFields[]) =>
  (state.parties.officers = [...(payload || [])]);

const setDraftPartySigners = (state: CreateState, payload: SignerPartyDetails) => (state.signerPartyDetails = { ...(payload || {}) });

const setDraftDocumentFolderDetails = (state: CreateState, payload: DocumentManagementFolder[]) =>
  (state.parties.documentFolderDetails = [...(payload || [])]);

const setDraftClientUploadedDocuments = (state: CreateState, payload: DocumentManagementFile) =>
  (state.clientUploadedDocuments = { ...(payload || {}) });

const setDraftFeaturesAndServices = (state: CreateState, payload: FeaturesAndServices) => {
  const {
    featuresSetDefault,
    cashSweepOptions,
    reportingParameters,
    taxParameters,
    servicesSetDefault,
    reportingAnalytics,
    dataFeed,
  } = payload;
  state.featuresAndServices.cashSweepOptions = [...(cashSweepOptions || [])];
  state.featuresAndServices.featuresSetDefault = featuresSetDefault || false;
  state.featuresAndServices.reportingParameters = [...(reportingParameters || [])];
  state.featuresAndServices.taxParameters = [...(taxParameters || [])];
  state.featuresAndServices.servicesSetDefault = servicesSetDefault || false;
  state.featuresAndServices.reportingAnalytics = [...(reportingAnalytics || [])];
  state.featuresAndServices.dataFeed = [...(dataFeed || [])];
};

const getDraftStepConfig = (draftStage: string, isEditMode: boolean): StepConfig[] => {
  const currentStep = Constants.createSteps.findIndex(step => step.route === draftStage);
  const normalizedCurrentStep = currentStep >= 0 ? currentStep : 0;

  return Constants.createSteps.map((step, index) => {
    let stepName = step.name;
    if (index === 1 && isEditMode) {
      stepName = 'Related Parties';
    } else if(index === 3 && isEditMode) {
      stepName = 'Review Details';
    }
    const updatedSteps = {
      ...step,
      completed: index < normalizedCurrentStep,
      currentStep: normalizedCurrentStep,
      hasError: false,
      name: stepName
    };
    return updatedSteps;
  });
};

export const createEntitySlice = createSlice({
  name: 'createEntity',
  initialState,
  reducers: {
    resetCreate: () => initialState,
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEditMode = action.payload;
    },
    setIsMaintenanceRequired: (state, action: PayloadAction<boolean>) => {
      state.isMaintenanceRequired = action.payload;
    },
    setDraftStage: (state, action: PayloadAction<string>) => {
      state.draftDetails.draftStage = action.payload;
    },
    setCreateEntityDetails: (state, action: PayloadAction<ClubbedEntityDetails>) => {
      state.entityDetails = { ...action.payload };
    },
    setCreateDocuments: (state, action: PayloadAction<ClientUploadStorePayload>) => {
      const { folderId, document } = action.payload;
      let uploadedArray = state.clientUploadedDocuments[folderId] || [];
      uploadedArray = uploadedArray.filter(item => item.name !== document.name); // Remove duplicates

      uploadedArray = [...uploadedArray, document];
      const updatedClientDocuments = { ...state.clientUploadedDocuments, [folderId]: uploadedArray };
      state.clientUploadedDocuments = { ...updatedClientDocuments };
    },
    updateDocumentCategory: (
      state,
      action: PayloadAction<{ folderId: string; documentId: string; category: string }>
    ) => {
      const { folderId, documentId, category } = action.payload;
      const uploadedArray = state.clientUploadedDocuments[folderId] || [];
      const updatedArray = uploadedArray.map((document: UploadDocument) => {
        if (document.id === documentId) {
          return { ...document, category };
        }
        return document;
      });
      const updatedClientDocuments = { ...state.clientUploadedDocuments, [folderId]: updatedArray };
      state.clientUploadedDocuments = { ...updatedClientDocuments };
    },
    deleteCreateDocuments: (state, action: PayloadAction<{ folderId: string, fileId: string }>) => {
      const { folderId, fileId } = action.payload;
      const uploadedArray: UploadDocument[] = state.clientUploadedDocuments[folderId] || [];
      const index = uploadedArray.findIndex((item: UploadDocument) => item.id === fileId);
      if (index >= 0) {
        uploadedArray.splice(index, 1);
      }
      const updatedClientDocuments = { ...state.clientUploadedDocuments, [folderId]: uploadedArray };
      state.clientUploadedDocuments = { ...updatedClientDocuments };
    },
    setSelectedParty: (state, action: PayloadAction<ISelectedParty>) => {
      state.parties.selectedParty = { ...state.parties.selectedParty, ...action.payload };
    },
    setSelectedControlPerson: (state, action: PayloadAction<OfficerRole>) => {
      state.parties.selectedControlPerson = action.payload;
    },
    setSelectedCorporateSecretary: (state, action: PayloadAction<OfficerRole>) => {
      state.parties.selectedCorporateSecretary = action.payload;
    },
    setBeneficialOwners: (state, action: PayloadAction<BeneficialOwnerFields>) => {
      state.parties.beneficialOwners = [...state.parties.beneficialOwners, action.payload];
    },
    updateBeneficialOwners: (state, action: PayloadAction<BeneficialOwnerFields>) => {
      const officerId: string | number | undefined = action?.payload?.officerId ?? '';
      const index = state.parties.beneficialOwners.findIndex(
        (beneficialOwner: BeneficialOwnerFields) => beneficialOwner?.officerId === officerId
      );
      if (index === -1 || index === undefined) {
        state.parties.beneficialOwners = [...state.parties.beneficialOwners, action.payload];
      } else {
        state.parties.beneficialOwners[index] = action.payload;
      }

      state.parties.officers = state.parties.officers?.map((officer: OfficerFields) =>
        officer?.officerId === officerId
          ? { ...officer, percentageOfOwnership: action.payload?.percentageOfOwnership }
          : officer
      );
    },
    setOfficers: (state, action: PayloadAction<OfficerFields>) => {
      const index = state.parties.officers.findIndex(
        (officer: OfficerFields) => officer?.officerId === action?.payload?.officerId
      );
      if (index >= 0) {
        state.parties.officers[index] = action.payload;
      } else {
        state.parties.officers = [...state.parties.officers, action.payload];
      }
    },
    deleteOfficer: (state, action: PayloadAction<{ id: NumberType }>) => {
      state.parties.officers = state.parties.officers.filter(
        (officer: OfficerFields) => officer?.officerId !== action?.payload?.id
      );
    },
    setFiduciary: (state, action: PayloadAction<FiduciaryFields>) => {
      const index = state.parties.fiduciary.findIndex(
        (officer: FiduciaryFields) => officer?.officerId === action?.payload?.officerId
      );
      if (index >= 0) {
        state.parties.fiduciary[index] = action.payload;
      } else {
        state.parties.fiduciary = [...state.parties.fiduciary, action.payload];
      }
    },
    deleteFiduciary: (state, action: PayloadAction<{ id: NumberType }>) => {
      state.parties.fiduciary = state.parties.fiduciary.filter(
        (fiduciary: FiduciaryFields) => fiduciary?.officerId !== action?.payload?.id
      );
    },
    setPowerHolding: (state, action: PayloadAction<IPowerHoldingTable[]>) => {
      state.parties.powerHoldingRole = action.payload;
    },
    deleteBeneficialOwners: (state, action: PayloadAction<{ id: NumberType }>) => {
      state.parties.beneficialOwners = state.parties.beneficialOwners.filter(
        (beneficialOwner: BeneficialOwnerFields) => beneficialOwner?.officerId !== action?.payload?.id
      );
      state.parties.officers = state.parties.officers.map((officer: OfficerFields) =>
        officer?.officerId === action?.payload?.id
          ? { ...officer, isBeneficialOwner: false, percentageOfOwnership: '' }
          : officer
      );
    },
    setAccountDetails: (state, action: PayloadAction<AccountDetails>) => {
      state.accountDetails = { ...state.accountDetails, ...action.payload };
    },
    setSignerPartyDetails: (state, action: PayloadAction<SignerPartyDetails>) => {
      state.signerPartyDetails = { ...state.signerPartyDetails, ...action.payload };
    },
    setCashSweepOption: (state, action: PayloadAction<ICashSweep>) => {
      state.featuresAndServices.cashSweepOptions = [action.payload];
    },
    deleteCashSweepOption: state => {
      state.featuresAndServices.cashSweepOptions = [];
    },
    setReportingParameter: (state, action: PayloadAction<IReportingParameters>) => {
      state.featuresAndServices.reportingParameters = [action.payload];
    },
    deleteReportingParameter: state => {
      state.featuresAndServices.reportingParameters = [];
    },
    setTaxParameter: (state, action: PayloadAction<ITaxParameter>) => {
      const index = state.featuresAndServices.taxParameters.findIndex(
        (item: ITaxParameter) => item?.taxProfileId === action?.payload?.taxProfileId
      );
      if (index === -1 || index === undefined) {
        state.featuresAndServices.taxParameters = [...state.featuresAndServices.taxParameters, action.payload];
      } else {
        state.featuresAndServices.taxParameters[index] = action.payload;
      }
    },
    deleteTaxParameter: (state, action: PayloadAction<{ id: NumberType }>) => {
      state.featuresAndServices.taxParameters = state.featuresAndServices.taxParameters.filter(
        (item: ITaxParameter) => item?.taxProfileId !== action?.payload?.id
      );
    },
    setReportingAnalytics: (state, action: PayloadAction<IReportingAnalytics>) => {
      state.featuresAndServices.reportingAnalytics = [action.payload];
    },
    deleteReportingAnalytics: state => {
      state.featuresAndServices.reportingAnalytics = [];
    },
    setDataFeed: (state, action: PayloadAction<IDataFeed>) => {
      state.featuresAndServices.dataFeed = [action.payload];
    },
    deleteDataFeed: state => {
      state.featuresAndServices.dataFeed = [];
    },
    setFundingData: (state, action: PayloadAction<IFundingDetails[]>) => {
      state.featuresAndServices.fundingData = [...action.payload];
    },
    deleteFundingData: (state, action: PayloadAction<{ id: NumberType }>) => {
      state.featuresAndServices.fundingData = state.featuresAndServices.fundingData.filter(
        (item: IFundingDetails) => item?.id !== action?.payload?.id
      );
    },
    setAuthorizedAgent: (state, action: PayloadAction<AuthorizedAgentsFormFields>) => {
      state.parties.authorizedAgents = [...state.parties.authorizedAgents, action.payload];
    },
    updateAuthorizedAgent: (state, action: PayloadAction<AuthorizedAgentsFormFields>) => {
      const agentId: string | number | undefined = action?.payload?.agentId ?? '';
      const index = state.parties.authorizedAgents.findIndex(
        (authorizedAgent: AuthorizedAgentsFormFields) => authorizedAgent?.agentId === agentId
      );
      if (index === -1 || index === undefined) {
        state.parties.authorizedAgents = [...state.parties.authorizedAgents, action.payload];
      } else {
        state.parties.authorizedAgents[index] = action.payload;
      }
    },
    deleteAuthorizedAgent: (state, action: PayloadAction<{ id: NumberType }>) => {
      state.parties.authorizedAgents = state.parties.authorizedAgents.filter(
        (authorizedAgent: AuthorizedAgentsFormFields) => authorizedAgent?.agentId !== action?.payload?.id
      );
    },
    setFeaturesSetDefault: (state, action: PayloadAction<boolean>) => {
      state.featuresAndServices.featuresSetDefault = action.payload;
    },
    setServicesSetDefault: (state, action: PayloadAction<boolean>) => {
      state.featuresAndServices.servicesSetDefault = action.payload;
    },
    setBeneficialOwnerCheck: (state, action: PayloadAction<boolean>) => {
      state.parties.beneficialOwnerCheck = action.payload;
    },
    setAccountAccess: (state, action: PayloadAction<AccountAccessData>) => {
      state.accountDetails.accountAccessData = action.payload;
    },
    setStepConfigData: (state, action: PayloadAction<StepConfig[]>) => {
      state.stepConfigData = action.payload;
    },
    setCoOwners: (state, action: PayloadAction<CoOwnerFields>) => {
      const index = state.parties.coOwners.findIndex(
        (coOwner: CoOwnerFields) => coOwner?.coOwnerId === action?.payload?.coOwnerId
      );
      if (index >= 0) {
        state.parties.coOwners[index] = action.payload;
      } else {
        state.parties.coOwners = [...state.parties.coOwners, action.payload];
      }
    },
    deleteCoOwner: (state, action: PayloadAction<{ id: NumberType }>) => {
      state.parties.coOwners = state.parties.coOwners.filter(
        (coOwner: CoOwnerFields) => coOwner?.coOwnerId !== action?.payload?.id
      );
    },
    updateMailingAddressList: (state, action: PayloadAction<Address>) => {
      state.prefillData.mailingAddressesList = [...(state.prefillData.mailingAddressesList || []), action.payload];
    },
    setCreateDraftData: (state, action: PayloadAction<CreateDraftDetails>) => {
      const {
        draftDetails,
        entityDetails,
        officers,
        controlPerson,
        corporateSecretary,
        beneficialOwners,
        beneficialOwnerCheck,
        authorizedAgents,
        coOwners,
        accountDetails,
        featuresSetDefault,
        cashSweepOptions,
        reportingParameters,
        taxParameters,
        servicesSetDefault,
        reportingAnalytics,
        dataFeed,
        fundingData,
        signerPartyDetails,
        documentFolderDetails,
        clientUploadedDocuments,
        fiduciary,
        powerHoldingRole,
        isMaintenanceRequired
      } = action.payload;
      state.isMaintenanceRequired = isMaintenanceRequired ?? false;
      state.stepConfigData = getDraftStepConfig(draftDetails?.draftStage ?? '', state.isEditMode);
      state.draftDetails = { ...((draftDetails as DraftDetails) || {}) };
      state.entityDetails = { ...(entityDetails || {}) };
      state.defaultEntityDetails = { ...(entityDetails || {}) };
      setDraftOfficers(state, officers as OfficerFields[]);
      state.parties.selectedControlPerson = { ...(controlPerson || {}) };
      state.parties.selectedCorporateSecretary = { ...(corporateSecretary || {}) };
      state.parties.beneficialOwners = [...(beneficialOwners || [])];
      state.parties.beneficialOwnerCheck = (beneficialOwnerCheck || false);
      state.parties.authorizedAgents = [...(authorizedAgents || [])];
      state.parties.coOwners = [...(coOwners || [])];
      state.accountDetails = { ...(accountDetails || {}) };
      state.parties.fiduciary = [...(fiduciary || [])];
      state.parties.powerHoldingRole = [...(powerHoldingRole || [])];
      setDraftFeaturesAndServices(state, {
        featuresSetDefault,
        cashSweepOptions,
        reportingParameters,
        taxParameters,
        servicesSetDefault,
        reportingAnalytics,
        dataFeed,
      });
      state.featuresAndServices.fundingData = [...(fundingData || [])];
      setDraftPartySigners(state, signerPartyDetails as SignerPartyDetails);
      setDraftDocumentFolderDetails(state, documentFolderDetails as DocumentManagementFolder[]);
      setDraftClientUploadedDocuments(state, clientUploadedDocuments as DocumentManagementFile);
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchEncryptionKey.fulfilled, (state, action) => {
      state.encryptionKey = action.payload as any;
    });
    builder.addCase(saveDraftData.pending, state => {
      state.draftDetails = { ...state.draftDetails, isDraftSaving: true };
    });
    builder.addCase(saveDraftData.fulfilled, (state, action) => {
      const resultSetInformation: DraftResponse[] = action.payload.resultSet as DraftResponse[];
      const [draftData] = resultSetInformation;
      state.draftDetails = { ...draftData, isDraftSaving: false };
    });
    builder.addCase(fetchReferenceCountries.fulfilled, (state, action) => {
      const countryList: ReferenceData[] = action.payload.resultSet as ReferenceData[];
      const usOnlyList = countryList.filter((country: ReferenceData) => country.value === 'US');
      state.prefillData.countryList = [...usOnlyList];
      state.prefillData.citizenshipList = [...(action.payload.resultSet as ReferenceData[])];
    });
    builder.addCase(fetchReferenceStates.fulfilled, (state, action) => {
      state.prefillData.statesList = [...(action.payload.resultSet as ReferenceData[])];
    });
    builder.addCase(fetchReferenceDataInformation.fulfilled, (state, action) => {
      const resultSetInformation: DataInformation[] = action.payload.resultSet as DataInformation[];
      const [refDataInformation] = resultSetInformation;
      state.prefillData.referenceData = { ...refDataInformation };
    });
    builder.addCase(fetchReferenceSubLegalEntity.fulfilled, (state, action) => {
      state.prefillData.subLegalEntityList = [...(action.payload.resultSet as ReferenceData[])];
    });
    builder.addCase(fetchMailingAddresses.fulfilled, (state, action) => {
      state.prefillData.mailingAddressesList = [...(action.payload.resultSet as Address[])];
    });
    builder.addCase(fetchAllEntitySubTypes.fulfilled, (state, action) => {
      const { corpEntityResultSet, trustEntityResultSet } = action.payload;
      state.prefillData.subLegalEntityListCorp = [...(corpEntityResultSet as ReferenceData[])];
      state.prefillData.subLegalEntityListTrust = [...(trustEntityResultSet as ReferenceData[])];
    });
    builder.addCase(fetchReferenceQualificationCode.fulfilled, (state, action) => {
      state.prefillData.qualificationCode = [...(action.payload.resultSet as ReferenceData[])];
    });
    builder.addCase(fetchRequiredDocuments.fulfilled, (state, action) => {
      if (action.payload) state.prefillData.requiredDocumentsList = [...(action.payload as ReferenceData[])];
    });
    builder.addCase(retrieveLegalEntity.fulfilled, (state, action) => {
      const resultSetInformation: RetrieveResultSet[] = action.payload as RetrieveResultSet[];
      const [entityRetrieve] = resultSetInformation;
      if (action.payload) state.entityRetrieveDetails = { ...entityRetrieve };
    });
    builder.addCase(retrieveAccount.fulfilled, (state, action) => {
      const resultSetInformation: RetrieveAccountResultSet[] = action.payload as RetrieveAccountResultSet[];
      const [accountRetrieve] = resultSetInformation;
      if (action.payload) state.accountRetrieveDetails = { ...accountRetrieve };
    });
    builder.addCase(fetchCoOwnershipList.fulfilled, (state, action) => {
      if (action.payload) state.prefillData.referenceData.refCoOwnershipList = [...(action.payload as ReferenceData[])];
    });
    builder.addCase(fetchDefaultedFeaturesAndServices.fulfilled, (state, action) => {
      const { cashSweepOptions, reportingParameters, taxParameters, reportingAnalytics, dataFeed } =
        getDefaultedFeaturesAndServices(
          action.payload as DraftAccountDetails[],
          state.prefillData.referenceData
        );

      setDraftFeaturesAndServices(state, {
        cashSweepOptions,
        reportingParameters,
        taxParameters,
        reportingAnalytics,
        dataFeed,
      });
    });
    builder.addCase(createDocumentManagementFolder.fulfilled, (state, action) => {
      if (action.payload)
        state.parties.documentFolderDetails = [
          ...state.parties.documentFolderDetails,
          ...(action.payload as DocumentManagementFolder[]),
        ];
    });
  },
});

// Action creators are generated for each case reducer function
export const {
  resetCreate,
  setDraftStage,
  setCreateDocuments,
  updateDocumentCategory,
  deleteCreateDocuments,
  setCreateEntityDetails,
  setSelectedParty,
  setSelectedControlPerson,
  setSelectedCorporateSecretary,
  setBeneficialOwners,
  setOfficers,
  setFiduciary,
  setPowerHolding,
  setAccountDetails,
  setSignerPartyDetails,
  setCashSweepOption,
  deleteCashSweepOption,
  setReportingParameter,
  deleteReportingParameter,
  setFundingData,
  deleteFundingData,
  setTaxParameter,
  deleteTaxParameter,
  deleteOfficer,
  deleteFiduciary,
  deleteBeneficialOwners,
  updateBeneficialOwners,
  setAuthorizedAgent,
  updateAuthorizedAgent,
  deleteAuthorizedAgent,
  setFeaturesSetDefault,
  setServicesSetDefault,
  setBeneficialOwnerCheck,
  setAccountAccess,
  setStepConfigData,
  setReportingAnalytics,
  deleteReportingAnalytics,
  setDataFeed,
  deleteDataFeed,
  setCoOwners,
  deleteCoOwner,
  setCreateDraftData,
  setEditMode,
  setIsMaintenanceRequired,
  updateMailingAddressList
} = createEntitySlice.actions;

export const selectStateData = (state: RootState) => state.createEntity;
export const selectEncryptionKey = (state: RootState) => state.createEntity.encryptionKey;
export const selectPrefillData = (state: RootState) => state.createEntity.prefillData;
export const selectCreateEntityDetails = (state: RootState) => state.createEntity.entityDetails;
export const selectDefaultEntityDetails = (state: RootState) => state.createEntity.defaultEntityDetails;
export const selectedExistingParty = (state: RootState) => state.createEntity.parties.selectedParty;
export const selectControlPerson = (state: RootState) => state.createEntity.parties.selectedControlPerson;
export const selectCorporateSecretary = (state: RootState) => state.createEntity.parties.selectedCorporateSecretary;
export const selectBeneficialOwners = (state: RootState) => state.createEntity.parties.beneficialOwners;
export const selectOfficers = (state: RootState) => state.createEntity.parties.officers;
export const selectFiduciary = (state: RootState) => state.createEntity.parties.fiduciary;
export const selectPowerHolding = (state: RootState) => state.createEntity.parties.powerHoldingRole;
export const selectCreateAccountDetails = (state: RootState) => state.createEntity.accountDetails;
export const selectCreateFeaturesAndServices = (state: RootState) => state.createEntity.featuresAndServices;
export const selectCreateSignerPartyDetails = (state: RootState) => state.createEntity.signerPartyDetails;
export const selectCashSweepOptions = (state: RootState) => state.createEntity.featuresAndServices.cashSweepOptions;
export const selectReportingParameters = (state: RootState) =>
  state.createEntity.featuresAndServices.reportingParameters;
export const selectFundingData = (state: RootState) => state.createEntity.featuresAndServices.fundingData;
export const selectTaxParameter = (state: RootState) => state.createEntity.featuresAndServices.taxParameters;
export const selectEntityRetrieveDetails = (state: RootState) => state.createEntity.entityRetrieveDetails;
export const selectAuthorizedAgents = (state: RootState) => state.createEntity.parties.authorizedAgents;
export const selectBeneficialOwnersCheck = (state: RootState) => state.createEntity.parties.beneficialOwnerCheck;
export const selectStepConfigData = (state: RootState) => state.createEntity.stepConfigData;
export const selectReportingAnalytics = (state: RootState) => state.createEntity.featuresAndServices.reportingAnalytics;
export const selectDataFeed = (state: RootState) => state.createEntity.featuresAndServices.dataFeed;
export const selectAccountRetrieveDetails = (state: RootState) => state.createEntity.accountRetrieveDetails;
export const selectCoOwners = (state: RootState) => state.createEntity.parties.coOwners;
export const selectDocumentFolder = (state: RootState) => state.createEntity.parties.documentFolderDetails;
export const selectClientUploadedDocuments = (state: RootState) => state.createEntity.clientUploadedDocuments;
export const selectRelatedParties = (state: RootState) => state.createEntity.parties;
export const selectIsEditMode = (state: RootState) => state.createEntity.isEditMode;
export const selectIsMaintenanceRequired = (state: RootState) => state.createEntity.isMaintenanceRequired;
export const selectIsDraftSaving = (state: RootState) => state.createEntity.draftDetails.isDraftSaving;
export const selectIsRelatedPartyMaintenanceRequired = (state: RootState) => {
  return state.createEntity.parties.officers.some(officer => officer.isMaintenanceRequired) ||
    state.createEntity.parties.fiduciary.some(
      fiduciary =>
        fiduciary.isMaintenanceRequired ||
        fiduciary.signerDetails?.some(signer => signer.isMaintenanceRequired)
    ) ||
    state.createEntity.parties.officers.some(officer => officer.signerDetails?.some(signer => signer.isMaintenanceRequired)) ||
    state.createEntity.parties.beneficialOwners.some(beneficialOwner => beneficialOwner.isMaintenanceRequired);
};

export default createEntitySlice.reducer;
